import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { EntityLink } from "scmp-app/components/entity-link";
import { getResponsiveProps } from "scmp-app/components/header/features/helpers";
import type { HeaderFeatureCommonProps } from "scmp-app/components/header/features/types";
import type { sectionEntityLinkHeaderFeaturesSectionQuery } from "scmp-app/queries/__generated__/sectionEntityLinkHeaderFeaturesSectionQuery.graphql";

import { Container } from "./styles";

export type entityLinkNameSize = "large" | "small";

export type HeaderFeaturesSectionEntityLinkProps = HeaderFeatureCommonProps & {
  isSubSection?: boolean;
  sectionId?: string | string[] | undefined;
  size?: entityLinkNameSize;
};

export const HeaderFeaturesSectionEntityLink: FunctionComponent<
  HeaderFeaturesSectionEntityLinkProps
> = ({ isSubSection, sectionId, size = "small", ...props }) => {
  const { section } = useLazyLoadQuery<sectionEntityLinkHeaderFeaturesSectionQuery>(
    graphql`
      query sectionEntityLinkHeaderFeaturesSectionQuery($hasEntityId: Boolean!, $entityId: String) {
        section(filter: { entityId: $entityId }) @include(if: $hasEntityId) {
          name
          fullSectionPath {
            name
            ...entityLink
          }
          ...entityLink
        }
      }
    `,
    {
      entityId: first(Array.isArray(sectionId) ? sectionId : [sectionId]),
      hasEntityId: notEmpty(sectionId),
    },
  );

  if (!notEmpty(section)) return null;

  const parentSectionData = section?.fullSectionPath?.at(-2);

  return (
    <Container $size={size} {...getResponsiveProps(props)}>
      {isSubSection && parentSectionData ? (
        <EntityLink reference={parentSectionData}>{parentSectionData.name}</EntityLink>
      ) : (
        section && <EntityLink reference={section}>{section.name}</EntityLink>
      )}
    </Container>
  );
};

HeaderFeaturesSectionEntityLink.displayName = "HeaderFeaturesSectionEntityLink";
